// src/components/Spreadsheet/GroupedSpreadsheetCell.jsx

import React, {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';

import {
  getColumnKey,
  normalizeString,
} from './groupedSpreadsheetUtils';

/**
 * GroupedSpreadsheetCell
 * -----------------------------------------------------------------------------
 * Reusable spreadsheet cell for the shared grouped spreadsheet engine.
 *
 * PURPOSE
 *  • Render one visual spreadsheet cell
 *  • Keep hot cell editing local while the user is typing
 *  • Commit changes only on intentional boundaries:
 *      - blur
 *      - Enter
 *      - Tab
 *      - explicit parent update
 *      - virtualization/unmount safety
 *  • Support text, date-text, select, read-only, and custom cells
 *  • Avoid domain assumptions:
 *      - no Form Lists logic
 *      - no SmartLists logic
 *      - no Firebase/context reads
 *
 * PERFORMANCE RULES
 *  • This component is memoized.
 *  • It keeps draft text local so each keystroke does not force parent
 *    spreadsheet state updates.
 *  • Parent receives updates only through onCommitCellValue(...).
 *  • Display-mode cells are NOT independently keyboard-focusable.
 *    The canvas owns keyboard navigation and focus.
 *  • Edit-mode inputs receive focus only while the cell is actively editing.
 *  • Custom cells are delegated to renderCustomCell(...).
 *
 * VIRTUALIZATION-READY RULES
 *  • Local uncommitted edits are committed on unmount.
 *  • Identity is passed from parent as groupId + rowId + columnKey.
 *  • Focus registration is independent of DOM index.
 *  • Display cells can unmount/remount without losing selection because
 *    selectedCell lives in the canvas.
 *
 * SPREADSHEET UX RULES
 *  • Single click selects.
 *  • Double click requests edit mode.
 *  • Canvas-level F2 / printable key can request edit mode.
 *  • Enter/Tab while editing commits and delegates navigation to the canvas.
 *  • Escape while editing cancels the draft.
 * -----------------------------------------------------------------------------
 */

const EMPTY_STRING = '';

/**
 * Shared spreadsheet body cell font size.
 *
 * WHY
 *  • SmartLists uses 11px for dense spreadsheet body cells.
 *  • Form Lists V2 should match the same spreadsheet density.
 *  • Column headers already use their own header styling and should remain
 *    unchanged.
 */
const SPREADSHEET_CELL_FONT_SIZE = '11px';

const getCellDisplayValue = (value) => {
  if (value == null) return EMPTY_STRING;
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  if (typeof value === 'boolean') return value ? 'true' : 'false';

  return String(value);
};

const normalizeCommittedValue = ({
  value,
  column,
  row,
}) => {
  if (typeof column?.normalizeCommittedValue === 'function') {
    return column.normalizeCommittedValue(value, row, column);
  }

  if (column?.type === 'number') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : '';
  }

  return value;
};

const areValuesEquivalent = (a, b) => {
  return getCellDisplayValue(a) === getCellDisplayValue(b);
};

const focusWithoutScroll = (element) => {
  if (!element || typeof element.focus !== 'function') return;

  try {
    element.focus({ preventScroll: true });
  } catch (error) {
    // Older browsers may not support the focus options object.
    // Fallback keeps the grid usable without coupling the shared
    // engine to browser capability checks.
    element.focus();
  }
};

/**
 * TEMP DEBUG
 * ---------------------------------------------------------------------------
 * Enable only while diagnosing rapid Enter → type handoff issues.
 *
 * Expected bug signature:
 *  • canvas sends editInitialValue: "o"
 *  • cell layout/focus runs while input.value is still ""
 *  • native onChange then receives "n"
 *  • final cell value becomes "ne"
 */
const DEBUG_CELL_TYPEAHEAD = false;

const logCellTypeahead = (label, payload = {}) => {
  if (!DEBUG_CELL_TYPEAHEAD) return;

  console.log(`[GroupedSpreadsheetCell:typeahead] ${label}`, payload);
};

const GroupedSpreadsheetCell = memo(({
  groupId,
  row,
  rowId,
  rowIndex,
  column,
  columnIndex,
  value,
  width,
  selected = false,
  readOnly = false,
  disabled = false,
  dirty = false,
  showDirtyCellIndicator = true,

  // Controlled edit-mode props supplied by the canvas.
  editing = false,
  editInitialValue = null,

  registerCellRef,
  onSelectCell,
  onCommitCellValue,
  onRequestCellEdit,
  onEndCellEdit,
  onEditInputReady,
  onKeyDown,
  onPaste,
  renderCustomCell,
}) => {
  const columnKey = getColumnKey(column);
  const cellRef = useRef(null);
  const inputRef = useRef(null);

  const isCustomCell = column?.type === 'custom';
  const isSelectCell = column?.type === 'select';
  const isReadOnly = readOnly || column?.readOnly === true || disabled;

  const committedDisplayValue = useMemo(
    () => getCellDisplayValue(value),
    [value],
  );

  /**
   * localEditing exists for backwards compatibility and local fallback.
   *
   * In the enterprise v2 model, the canvas should control edit mode through
   * the `editing` prop. However, keeping a local fallback lets this cell remain
   * usable in isolated test harnesses.
   */
  const [localEditing, setLocalEditing] = useState(false);
  const isEditing = editing || localEditing;

  const [draftValue, setDraftValue] = useState(committedDisplayValue);

  const latestDraftValueRef = useRef(committedDisplayValue);
  const latestCommitContextRef = useRef(null);
  const hasUncommittedEditRef = useRef(false);
  const editFinishResetTimeoutRef = useRef(null);
  const isFinishingEditRef = useRef(false);
  const wasEditingRef = useRef(false);

  /**
   * Typeahead → native input handoff
   * ---------------------------------------------------------------------------
   * Canvas can buffer printable keys before the edit input is fully mounted.
   * The cell should keep accepting updated editInitialValue while the input has
   * not yet received native input.
   *
   * Once handleTextChange fires, the native input has taken over and the cell
   * must stop applying incoming editInitialValue updates to avoid overwriting
   * real user typing.
   */
  const hasNativeInputDuringEditRef = useRef(false);
  const lastAppliedEditInitialValueRef = useRef(null);

  /**
   * ENTERPRISE EDIT-SESSION VALUE MODEL
   * ---------------------------------------------------------------------------
   * Spreadsheet typeahead cannot wait for a normal useEffect to populate the
   * first edit value.
   *
   * WHY
   *  • A selected cell can enter edit mode from a printable key.
   *  • That printable key is supplied by the canvas as editInitialValue.
   *  • The TextField must render with that value immediately on the first edit
   *    render, before focus and before onEditInputReady clears the canvas
   *    typeahead buffer.
   *
   * RULE
   *  • Before native input has taken over, render editInitialValue directly.
   *  • After native onChange fires, draftValue becomes the source of truth.
   */
  const normalizedEditInitialValue = useMemo(
    () => (editInitialValue == null ? null : String(editInitialValue)),
    [editInitialValue],
  );

  const shouldUseInitialValueForRender =
    isEditing &&
    normalizedEditInitialValue != null &&
    !hasNativeInputDuringEditRef.current &&
    (
      !wasEditingRef.current ||
      normalizedEditInitialValue !== lastAppliedEditInitialValueRef.current
    );

  const renderedDraftValue = shouldUseInitialValueForRender
    ? normalizedEditInitialValue
    : draftValue;

  const renderedDraftValueRef = useRef(renderedDraftValue);
  const shouldUseInitialValueForRenderRef = useRef(
    shouldUseInitialValueForRender,
  );

  /**
   * Keep refs synchronized with the value that is actually rendered into the
   * edit input.
   *
   * IMPORTANT
   *  • This is useLayoutEffect on purpose.
   *  • It runs before the focus layout effect below.
   *  • That guarantees latestDraftValueRef matches the TextField's first
   *    rendered value before onEditInputReady can clear the canvas buffer.
   */
  useLayoutEffect(() => {
    renderedDraftValueRef.current = renderedDraftValue;
    shouldUseInitialValueForRenderRef.current = shouldUseInitialValueForRender;
    latestDraftValueRef.current = renderedDraftValue;

    if (isEditing && shouldUseInitialValueForRender) {
      hasUncommittedEditRef.current = true;
    }
  }, [
    isEditing,
    renderedDraftValue,
    shouldUseInitialValueForRender,
  ]);

  useEffect(() => {
    latestCommitContextRef.current = {
      isReadOnly,
      onCommitCellValue,
      column,
      row,
      value,
      groupId,
      rowId,
      rowIndex,
      columnKey,
      columnIndex,
    };
  }, [
    isReadOnly,
    onCommitCellValue,
    column,
    row,
    value,
    groupId,
    rowId,
    rowIndex,
    columnKey,
    columnIndex,
  ]);

  useEffect(() => {
    if (isEditing) return;

    if (!areValuesEquivalent(draftValue, committedDisplayValue)) {
      setDraftValue(committedDisplayValue);
      latestDraftValueRef.current = committedDisplayValue;
    }

    // We intentionally do not include draftValue here. Including it would
    // reset local typing on every keystroke. This effect only reacts when the
    // parent committed value changes and the cell is not editing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    committedDisplayValue,
    isEditing,
  ]);

  useEffect(() => {
    const justStartedEditing = isEditing && !wasEditingRef.current;
    const incomingInitialValue = normalizedEditInitialValue;

    if (isEditing || incomingInitialValue != null || wasEditingRef.current) {
      logCellTypeahead('editInitialValue effect start', {
        groupId,
        rowId,
        rowIndex,
        columnKey,
        columnIndex,
        isEditing,
        wasEditing: wasEditingRef.current,
        justStartedEditing,
        editInitialValue,
        incomingInitialValue,
        committedDisplayValue,
        renderedDraftValue: renderedDraftValueRef.current,
        latestDraftValueRef: latestDraftValueRef.current,
        shouldUseInitialValueForRender:
          shouldUseInitialValueForRenderRef.current,
        hasNativeInputDuringEdit: hasNativeInputDuringEditRef.current,
        lastAppliedEditInitialValue: lastAppliedEditInitialValueRef.current,
      });
    }

    if (justStartedEditing) {
      const nextDraft =
        incomingInitialValue == null
          ? committedDisplayValue
          : incomingInitialValue;

      hasNativeInputDuringEditRef.current = false;
      lastAppliedEditInitialValueRef.current = incomingInitialValue;
      setDraftValue(nextDraft);
      latestDraftValueRef.current = nextDraft;
      hasUncommittedEditRef.current = incomingInitialValue != null;

      logCellTypeahead('justStartedEditing applied draft', {
        groupId,
        rowId,
        rowIndex,
        columnKey,
        columnIndex,
        incomingInitialValue,
        nextDraft,
        latestDraftValueRef: latestDraftValueRef.current,
        hasUncommittedEdit: hasUncommittedEditRef.current,
      });
    } else if (
      isEditing &&
      incomingInitialValue != null &&
      incomingInitialValue !== lastAppliedEditInitialValueRef.current &&
      !hasNativeInputDuringEditRef.current
    ) {
      /**
       * Canvas typeahead update while the input is mounting.
       *
       * Example:
       *  • user presses Enter from previous cell
       *  • immediately types "five"
       *  • Canvas may emit editInitialValue: "f", then "fi", then "fiv",
       *    then "five" before the native input fully owns keystrokes.
       *
       * Until native input fires onChange, accept the latest buffered value.
       */
      lastAppliedEditInitialValueRef.current = incomingInitialValue;
      setDraftValue(incomingInitialValue);
      latestDraftValueRef.current = incomingInitialValue;
      hasUncommittedEditRef.current = true;

      logCellTypeahead('typeahead update applied before native input', {
        groupId,
        rowId,
        rowIndex,
        columnKey,
        columnIndex,
        incomingInitialValue,
        latestDraftValueRef: latestDraftValueRef.current,
        hasNativeInputDuringEdit: hasNativeInputDuringEditRef.current,
        lastAppliedEditInitialValue: lastAppliedEditInitialValueRef.current,
      });
    }

    if (!isEditing) {
      hasNativeInputDuringEditRef.current = false;
      lastAppliedEditInitialValueRef.current = null;
    }

    wasEditingRef.current = isEditing;
  }, [
    isEditing,
    normalizedEditInitialValue,
    committedDisplayValue,
    editInitialValue,
    groupId,
    rowId,
    rowIndex,
    columnKey,
    columnIndex,
  ]);

  useLayoutEffect(() => {
    if (!isEditing) return undefined;

    const inputElement = inputRef.current;
    const currentRenderedDraftValue = renderedDraftValueRef.current;

    logCellTypeahead('layout effect before focus', {
      groupId,
      rowId,
      rowIndex,
      columnKey,
      columnIndex,
      inputExists: !!inputElement,
      inputValueBeforeFocus: inputElement?.value,
      currentRenderedDraftValue,
      latestDraftValueRef: latestDraftValueRef.current,
      shouldUseInitialValueForRender:
        shouldUseInitialValueForRenderRef.current,
      hasNativeInputDuringEdit: hasNativeInputDuringEditRef.current,
      lastAppliedEditInitialValue: lastAppliedEditInitialValueRef.current,
    });

    if (
      inputElement &&
      typeof inputElement.focus === 'function'
    ) {
      focusWithoutScroll(inputElement);

      if (
        inputElement.setSelectionRange &&
        typeof currentRenderedDraftValue === 'string'
      ) {
        const length = currentRenderedDraftValue.length;
        inputElement.setSelectionRange(length, length);
      }

      /**
       * Notify the canvas that the edit input is mounted/focused and can now
       * own native keyboard input.
       *
       * WHY
       *  • Canvas typeahead captures rapid printable keys before this input is
       *    ready.
       *  • Once this fires, the canvas can safely clear the typeahead buffer.
       *  • This avoids frame-timed buffer clearing, which can drop characters
       *    during fast Enter → type workflows.
       */
      if (typeof onEditInputReady === 'function') {
        logCellTypeahead('calling onEditInputReady', {
          groupId,
          rowId,
          rowIndex,
          columnKey,
          columnIndex,
          inputValueAtReady: inputElement?.value,
          currentRenderedDraftValue,
          latestDraftValueRef: latestDraftValueRef.current,
          shouldUseInitialValueForRender:
            shouldUseInitialValueForRenderRef.current,
          hasNativeInputDuringEdit: hasNativeInputDuringEditRef.current,
          lastAppliedEditInitialValue: lastAppliedEditInitialValueRef.current,
        });

        onEditInputReady({
          groupId,
          rowId,
          rowIndex,
          columnKey,
          columnIndex,
          value: currentRenderedDraftValue,
          row,
          column,
        });
      }
    }

    return undefined;
  }, [
    isEditing,
    onEditInputReady,
    groupId,
    rowId,
    rowIndex,
    columnKey,
    columnIndex,
    row,
    column,
  ]);

  useEffect(() => {
    return () => {
      if (editFinishResetTimeoutRef.current) {
        window.clearTimeout(editFinishResetTimeoutRef.current);
        editFinishResetTimeoutRef.current = null;
      }

      if (!hasUncommittedEditRef.current) return;

      const context = latestCommitContextRef.current;
      if (!context || context.isReadOnly || !context.onCommitCellValue) {
        return;
      }

      const nextValue = normalizeCommittedValue({
        value: latestDraftValueRef.current,
        column: context.column,
        row: context.row,
      });

      if (areValuesEquivalent(context.value, nextValue)) {
        return;
      }

      context.onCommitCellValue({
        groupId: context.groupId,
        rowId: context.rowId,
        rowIndex: context.rowIndex,
        columnKey: context.columnKey,
        columnIndex: context.columnIndex,
        value: nextValue,
        previousValue: context.value,
        row: context.row,
        column: context.column,
      });
    };
  }, []);

  useEffect(() => {
    if (!registerCellRef || !groupId || !rowId || !columnKey) return undefined;

    return registerCellRef({
      groupId,
      rowId,
      columnKey,
      element: cellRef.current,

      // In view mode, the canvas owns keyboard focus. We do not focus the cell.
      // In edit mode, focus the inner input only.
      focus: () => {
        if (
          isEditing &&
          inputRef.current &&
          typeof inputRef.current.focus === 'function'
        ) {
          focusWithoutScroll(inputRef.current);
        }
      },

      /**
       * Pending edit flush
       * -----------------------------------------------------------------------
       * Lets the parent canvas commit the currently active edit before an
       * external boundary reads a workspace snapshot.
       *
       * WHY
       *  • Route-exit / Save Draft / Publish can happen while a text input is
       *    still active.
       *  • Blur usually commits the value, and unmount cleanup is a safety net,
       *    but enterprise save boundaries should not rely on browser event
       *    ordering.
       *  • This keeps the shared Spreadsheet engine domain-free while giving
       *    adapters a deterministic "commit current edit first" API.
       */
      flushPendingEdit: ({
        reason = 'external_flush',
      } = {}) => {
        if (!hasUncommittedEditRef.current) {
          return {
            status: 'skipped',
            reason: 'no_uncommitted_edit',
          };
        }

        const context = latestCommitContextRef.current;

        if (!context || context.isReadOnly || !context.onCommitCellValue) {
          hasUncommittedEditRef.current = false;

          return {
            status: 'skipped',
            reason: 'missing_commit_context',
          };
        }

        const nextValue = normalizeCommittedValue({
          value: latestDraftValueRef.current,
          column: context.column,
          row: context.row,
        });

        if (areValuesEquivalent(context.value, nextValue)) {
          hasUncommittedEditRef.current = false;

          return {
            status: 'skipped',
            reason: 'value_unchanged',
          };
        }

        context.onCommitCellValue({
          groupId: context.groupId,
          rowId: context.rowId,
          rowIndex: context.rowIndex,
          columnKey: context.columnKey,
          columnIndex: context.columnIndex,
          value: nextValue,
          previousValue: context.value,
          row: context.row,
          column: context.column,
          source: reason,
        });

        hasUncommittedEditRef.current = false;

        return {
          status: 'success',
          reason,
          groupId: context.groupId,
          rowId: context.rowId,
          columnKey: context.columnKey,
        };
      },
    });
  }, [
    registerCellRef,
    groupId,
    rowId,
    columnKey,
    isEditing,
  ]);

  const commitValue = useCallback((nextRawValue) => {
    if (isReadOnly || !onCommitCellValue) {
      hasUncommittedEditRef.current = false;
      return;
    }

    const nextValue = normalizeCommittedValue({
      value: nextRawValue,
      column,
      row,
    });

    if (areValuesEquivalent(value, nextValue)) {
      hasUncommittedEditRef.current = false;
      return;
    }

    onCommitCellValue({
      groupId,
      rowId,
      rowIndex,
      columnKey,
      columnIndex,
      value: nextValue,
      previousValue: value,
      row,
      column,
    });

    hasUncommittedEditRef.current = false;
  }, [
    isReadOnly,
    onCommitCellValue,
    column,
    row,
    value,
    groupId,
    rowId,
    rowIndex,
    columnKey,
    columnIndex,
  ]);

  const handleSelectCell = useCallback(() => {
    if (!onSelectCell) return;

    onSelectCell({
      groupId,
      rowId,
      rowIndex,
      columnKey,
      columnIndex,
      row,
      column,
    });
  }, [
    onSelectCell,
    groupId,
    rowId,
    rowIndex,
    columnKey,
    columnIndex,
    row,
    column,
  ]);

  const requestEdit = useCallback((initialValue = null) => {
    if (isReadOnly || isCustomCell) return;

    const nextDraft =
      initialValue == null ? committedDisplayValue : String(initialValue);

    setDraftValue(nextDraft);
    latestDraftValueRef.current = nextDraft;
    hasUncommittedEditRef.current = initialValue != null;

    if (typeof onRequestCellEdit === 'function') {
      onRequestCellEdit({
        groupId,
        rowId,
        rowIndex,
        columnKey,
        columnIndex,
        initialValue,
        row,
        column,
      });
      return;
    }

    setLocalEditing(true);
  }, [
    isReadOnly,
    isCustomCell,
    committedDisplayValue,
    onRequestCellEdit,
    groupId,
    rowId,
    rowIndex,
    columnKey,
    columnIndex,
    row,
    column,
  ]);

  const finishEditing = useCallback(({
    commit = true,
    navigateEvent = null,
  } = {}) => {
    /**
     * Duplicate finish guard
     * -------------------------------------------------------------------------
     * Enter/Tab commits from keydown, then the old input can still emit blur
     * as it loses focus or unmounts.
     *
     * That blur is a duplicate edit boundary, not a second user intent.
     * Ignore it before doing any commit work or debug logging.
     */
    if (isFinishingEditRef.current) {
      return;
    }

    logCellTypeahead('finishEditing start', {
      groupId,
      rowId,
      rowIndex,
      columnKey,
      columnIndex,
      commit,
      navigateKey: navigateEvent?.key,
      latestDraftValueRef: latestDraftValueRef.current,
      hasNativeInputDuringEdit: hasNativeInputDuringEditRef.current,
      lastAppliedEditInitialValue: lastAppliedEditInitialValueRef.current,
      isFinishingEdit: isFinishingEditRef.current,
    });

    isFinishingEditRef.current = true;

    if (editFinishResetTimeoutRef.current) {
      window.clearTimeout(editFinishResetTimeoutRef.current);
      editFinishResetTimeoutRef.current = null;
    }

    if (commit) {
      commitValue(latestDraftValueRef.current);
    } else {
      setDraftValue(committedDisplayValue);
      latestDraftValueRef.current = committedDisplayValue;
      hasUncommittedEditRef.current = false;
    }

    hasNativeInputDuringEditRef.current = false;
    lastAppliedEditInitialValueRef.current = null;

    setLocalEditing(false);

    /**
     * ENTERPRISE EDIT-HANDOFF ORDER
     * -------------------------------------------------------------------------
     * For spreadsheet UX, Enter/Tab must move the selected cell before the
     * canvas focus is restored.
     *
     * If we call onEndCellEdit(...) first, the canvas can regain focus while
     * the selected cell is still the old cell. Immediate printable typing after
     * Enter can then be lost or sent to the wrong edit lifecycle.
     *
     * Correct order:
     *  1. Commit/cancel the local draft.
     *  2. Forward Enter/Tab navigation to the canvas.
     *  3. End edit mode and restore canvas focus.
     *  4. Suppress the duplicate blur finish cycle from the old input.
     */
    if (navigateEvent && typeof onKeyDown === 'function') {
      onKeyDown(navigateEvent, {
        groupId,
        rowId,
        rowIndex,
        columnKey,
        columnIndex,
        value: latestDraftValueRef.current,
        row,
        column,

        // Canvas uses this to navigate even though the edit-mode input
        // already prevented the browser's default Enter/Tab behavior.
        forceNavigation: true,
      });
    }

    if (typeof onEndCellEdit === 'function') {
      onEndCellEdit({
        groupId,
        rowId,
        rowIndex,
        columnKey,
        columnIndex,
        commit,
        row,
        column,
      });
    }

    editFinishResetTimeoutRef.current = window.setTimeout(() => {
      isFinishingEditRef.current = false;
      editFinishResetTimeoutRef.current = null;
    }, 0);
  }, [
    commitValue,
    committedDisplayValue,
    onEndCellEdit,
    onKeyDown,
    groupId,
    rowId,
    rowIndex,
    columnKey,
    columnIndex,
    row,
    column,
  ]);

  const handleDisplayMouseDown = useCallback((event) => {
    // Prevent the browser from moving DOM focus to the cell. The canvas owns
    // view-mode focus and keyboard handling.
    event.preventDefault();
    handleSelectCell();
  }, [handleSelectCell]);

  const handleDisplayDoubleClick = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();

    handleSelectCell();
    requestEdit();
  }, [
    handleSelectCell,
    requestEdit,
  ]);

  const handleTextChange = useCallback((event) => {
    const nextValue = event.target.value;

    logCellTypeahead('native input change', {
      groupId,
      rowId,
      rowIndex,
      columnKey,
      columnIndex,
      nextValue,
      renderedDraftValueBeforeChange: renderedDraftValueRef.current,
      previousLatestDraftValueRef: latestDraftValueRef.current,
      shouldUseInitialValueForRender:
        shouldUseInitialValueForRenderRef.current,
      hasNativeInputDuringEditBefore: hasNativeInputDuringEditRef.current,
      lastAppliedEditInitialValue: lastAppliedEditInitialValueRef.current,
    });

    // Once native input is receiving change events, it owns the edit draft.
    // Canvas typeahead must no longer overwrite draftValue.
    hasNativeInputDuringEditRef.current = true;

    hasUncommittedEditRef.current = true;
    latestDraftValueRef.current = nextValue;
    setDraftValue(nextValue);
  }, [
    groupId,
    rowId,
    rowIndex,
    columnKey,
    columnIndex,
  ]);

  const handleTextBlur = useCallback(() => {
    /**
     * Blur can fire immediately after Enter/Tab has already started finishing
     * the edit session.
     *
     * In that case, the blur is not a new commit boundary. It is a browser
     * focus side effect from the input losing focus while the canvas navigates
     * to the next cell.
     */
    if (isFinishingEditRef.current) {
      return;
    }

    finishEditing({ commit: true });
  }, [finishEditing]);

  const handleSelectChange = useCallback((event) => {
    const nextValue = event.target.value;
    setDraftValue(nextValue);
    latestDraftValueRef.current = nextValue;
    hasUncommittedEditRef.current = true;
    commitValue(nextValue);
  }, [commitValue]);

  const handleEditKeyDown = useCallback((event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      finishEditing({ commit: false });
      return;
    }

    if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault();

      finishEditing({
        commit: true,
        navigateEvent: event,
      });
    }
  }, [finishEditing]);

  const handlePaste = useCallback((event) => {
    if (isEditing) {
      return;
    }

    if (onPaste) {
      onPaste(event, {
        groupId,
        rowId,
        rowIndex,
        columnKey,
        columnIndex,
        row,
        column,
      });
    }
  }, [
    isEditing,
    onPaste,
    groupId,
    rowId,
    rowIndex,
    columnKey,
    columnIndex,
    row,
    column,
  ]);

  const sharedCellSx = useMemo(() => ({
    width,
    minWidth: width,
    maxWidth: width,
    height: '100%',
    minHeight: 24,
    boxSizing: 'border-box',
    borderRight: '1px solid',
    borderBottom: '1px solid',
    borderColor: 'divider',
    backgroundColor: selected ? 'action.selected' : 'background.paper',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    outline: selected ? '2px solid' : 'none',
    outlineColor: selected ? 'primary.main' : 'transparent',
    outlineOffset: selected ? '-2px' : 0,
    opacity: disabled ? 0.5 : 1,
    cursor: isReadOnly ? 'default' : 'cell',
    /**
     * Dirty-cell visual indicator
     * -------------------------------------------------------------------------
     * Dirty tracking is owned by the shared Spreadsheet engine and remains
     * active regardless of whether this visual marker is displayed.
     *
     * WHY THIS IS CONFIGURABLE
     *  • Some spreadsheet workspaces may want cell-level dirty affordances.
     *  • Form Lists Lab has many small cells and grouped dropdown columns, so
     *    per-cell dots create visual noise and extra paint work as edits grow.
     *  • Form Lists can surface dirty state at the workspace/group level instead
     *    without changing save/publish correctness.
     *
     * IMPORTANT
     *  • This does NOT change dirtyState.
     *  • This does NOT change snapshots.
     *  • This does NOT change Save Draft / Publish behavior.
     *  • This is presentation only.
     */
    '&::after': dirty && showDirtyCellIndicator
      ? {
          content: '""',
          position: 'absolute',
          top: 3,
          right: 3,
          width: 5,
          height: 5,
          borderRadius: '50%',
          bgcolor: 'primary.main',
        }
      : {},
  }), [
    width,
    selected,
    disabled,
    dirty,
    showDirtyCellIndicator,
    isReadOnly,
  ]);

  if (!columnKey) {
    return null;
  }

  if (isCustomCell && typeof renderCustomCell === 'function') {
    return (
      <Box
        ref={cellRef}
        role="gridcell"
        aria-selected={selected}
        onMouseDown={handleDisplayMouseDown}
        onPaste={handlePaste}
        sx={sharedCellSx}
      >
        {renderCustomCell({
          groupId,
          row,
          rowId,
          rowIndex,
          column,
          columnKey,
          columnIndex,
          value,
          selected,
          readOnly: isReadOnly,
          disabled,
        })}
      </Box>
    );
  }

  if (isReadOnly) {
    return (
      <Box
        ref={cellRef}
        role="gridcell"
        aria-selected={selected}
        onMouseDown={handleDisplayMouseDown}
        onPaste={handlePaste}
        sx={{
          ...sharedCellSx,
          px: 1,

          /**
           * Read-only spreadsheet value styling
           * -------------------------------------------------------------------
           * Read-only cells should not look like normal editable cells.
           *
           * WHY
           *  • Form Lists can render org-managed SmartList options.
           *  • Those option values are centrally governed and cannot be edited
           *    from the form-specific Lists Lab spreadsheet.
           *  • The user still needs to see the values and use them for
           *    form-specific dependencies, but the value cell should clearly
           *    communicate read-only behavior.
           *
           * IMPORTANT
           *  • This is shared spreadsheet presentation, not SmartLists logic.
           *  • The FormLists page decides whether the cell is read-only.
           *  • This cell only renders the correct read-only visual state.
           *  • Background color intentionally comes from sharedCellSx so read-only
           *    cells keep the same sheet surface as editable cells. The disabled
           *    affordance is communicated through text color only, while selected
           *    cells still use the standard spreadsheet selection background.
           */
          color: 'text.disabled',
          cursor: 'default',
          userSelect: 'none',
        }}
      >
        <Typography
          variant="caption"
          noWrap
          sx={{
            width: '100%',
            fontSize: SPREADSHEET_CELL_FONT_SIZE,
            lineHeight: 1.4,
            color: 'inherit',
          }}
        >
          {committedDisplayValue || '\u00A0'}
        </Typography>
      </Box>
    );
  }

  if (isSelectCell && isEditing) {
    const options = Array.isArray(column?.options) ? column.options : [];

    return (
      <Box
        ref={cellRef}
        role="gridcell"
        aria-selected={selected}
        onMouseDown={handleDisplayMouseDown}
        sx={sharedCellSx}
      >
        <Select
          inputRef={inputRef}
          value={draftValue}
          onChange={handleSelectChange}
          onBlur={() => finishEditing({ commit: true })}
          size="small"
          variant="standard"
          disableUnderline
          fullWidth
          sx={{
            height: '100%',
            px: 0.75,
            fontSize: SPREADSHEET_CELL_FONT_SIZE,
            '& .MuiSelect-select': {
              py: 0,
              minHeight: 'unset',
              display: 'flex',
              alignItems: 'center',
              fontSize: SPREADSHEET_CELL_FONT_SIZE,
            },
          }}
        >
          {options.map((option) => {
            const optionValue = normalizeString(option?.value, '');
            const optionLabel = normalizeString(
              option?.label,
              optionValue,
            );

            return (
              <MenuItem
                key={optionValue || optionLabel}
                value={optionValue}
                sx={{
                  fontSize: SPREADSHEET_CELL_FONT_SIZE,
                }}
              >
                {optionLabel}
              </MenuItem>
            );
          })}
        </Select>
      </Box>
    );
  }

  if (!isEditing) {
    return (
      <Box
        ref={cellRef}
        role="gridcell"
        aria-selected={selected}
        onMouseDown={handleDisplayMouseDown}
        onDoubleClick={handleDisplayDoubleClick}
        onPaste={handlePaste}
        sx={{
          ...sharedCellSx,
          px: 0.75,
        }}
      >
        <Typography
          variant="caption"
          noWrap
          sx={{
            width: '100%',
            fontSize: SPREADSHEET_CELL_FONT_SIZE,
            lineHeight: 1.4,
            color: committedDisplayValue ? 'text.primary' : 'transparent',
            userSelect: 'none',
          }}
        >
          {committedDisplayValue || '\u00A0'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={cellRef}
      role="gridcell"
      aria-selected={selected}
      sx={sharedCellSx}
    >
      <TextField
        inputRef={inputRef}
        value={renderedDraftValue}
        onChange={handleTextChange}
        onBlur={handleTextBlur}
        onKeyDown={handleEditKeyDown}
        variant="standard"
        fullWidth
        InputProps={{
          disableUnderline: true,
        }}
        inputProps={{
          'aria-label': column?.label || columnKey,
          spellCheck: false,
        }}
        sx={{
          height: '100%',
          '& .MuiInputBase-root': {
            height: '100%',
            fontSize: SPREADSHEET_CELL_FONT_SIZE,
            px: 0.75,
          },
          '& input': {
            py: 0,
            height: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontSize: SPREADSHEET_CELL_FONT_SIZE,
          },
        }}
      />
    </Box>
  );
});

GroupedSpreadsheetCell.displayName = 'GroupedSpreadsheetCell';

GroupedSpreadsheetCell.propTypes = {
  groupId: PropTypes.string.isRequired,
  row: PropTypes.object.isRequired,
  rowId: PropTypes.string.isRequired,
  rowIndex: PropTypes.number.isRequired,
  column: PropTypes.object.isRequired,
  columnIndex: PropTypes.number.isRequired,
  value: PropTypes.any,
  width: PropTypes.number.isRequired,
  selected: PropTypes.bool,
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  dirty: PropTypes.bool,
  showDirtyCellIndicator: PropTypes.bool,
  editing: PropTypes.bool,
  editInitialValue: PropTypes.any,
  registerCellRef: PropTypes.func,
  onSelectCell: PropTypes.func,
  onCommitCellValue: PropTypes.func,
  onRequestCellEdit: PropTypes.func,
  onEndCellEdit: PropTypes.func,
  onEditInputReady: PropTypes.func,
  onKeyDown: PropTypes.func,
  onPaste: PropTypes.func,
  renderCustomCell: PropTypes.func,
};

export default GroupedSpreadsheetCell;